import React from 'react';
import { type PaddingSize } from '../../utils/builderPaddingOptions';
import { MedicalDirector, Metro, State, Store } from 'clinics/clinics';
import { S40Breadcrumbs } from '@milanlaser/mlcomponents';

type BuilderState<T> = {
    device: string;
    deviceSize: string;
    isBrowser: boolean;
    isServer: boolean;
} & T;

type BuilderProps<T> = {
    builderState: {
        state: BuilderState<T>;
    };
};

type S40BreadcrumbsProps = BuilderProps<{
    metro: Metro;
    metroThumbnails: Record<string, string>;
    state: State;
    store: Store;
    staff: MedicalDirector[];
    model: string;
}> & {
    phoneIcon: string;
    mapIcon: string;
    desktopPaddingTop?: PaddingSize;
    desktopPaddingBottom?: PaddingSize;
    mobilePaddingTop?: PaddingSize;
    mobilePaddingBottom?: PaddingSize;
};

export function S40BreadcrumbsSection(props: S40BreadcrumbsProps) {
    const {
        desktopPaddingTop = 'large',
        desktopPaddingBottom = 'large',
        mobilePaddingTop = 'small',
        mobilePaddingBottom = 'small',
        ...restProps
    } = props;

    const {
        builderState: {
            state: { state, metro, store, model },
        },
    } = restProps;

    // Location data is only provided by the clinic/metro templates (clinic pages also need
    // the store). If it's missing, render nothing instead of failing the build.
    if (!state || !metro || (model === 'clinic-page' && !store)) {
        return null;
    }

    const stateName = state.name.length === 2 ? state.name.toUpperCase() : state.name;
    const stateAbbreviation = state.abbreviation.toLowerCase();
    const firstStoreWithUrlPath = metro.stores?.find((store) => store.urlPath);
    if (!firstStoreWithUrlPath || !firstStoreWithUrlPath.urlPath) {
        throw new Error(`No urlPath found for metro: ${metro.name}. All stores should have urlPath property.`);
    }

    const urlParts = firstStoreWithUrlPath.urlPath.split('/');
    const metroUrl = urlParts.slice(0, -1).join('/');

    if (!metroUrl) {
        throw new Error(
            `Invalid urlPath format for metro: ${metro.name}. Expected format: /locations/state/metro/store`
        );
    }

    const segments = [
        { label: 'Home', href: '/' },
        { label: 'Locations', href: '/locations' },
        { label: stateName, href: `/locations/${stateAbbreviation}` },
    ];

    const metroBreadcrumb = { label: metro.name, href: metroUrl };

    if (model === 'metro-page') {
        segments.push(metroBreadcrumb);
    }

    if (model === 'clinic-page') {
        const cityBreadcrumb = { label: store.marketingCity, href: store.urlPath };
        segments.push(metroBreadcrumb, cityBreadcrumb);
    }

    return (
        <div className="mcl-mx-auto pb-8 pt-12 mcl-px-4 md:mcl-px-6 xl:mcl-px-4">
            <div className="container">
                <S40Breadcrumbs segments={segments} />
            </div>
        </div>
    );
}
